import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { FreeCamera } from '@babylonjs/core/Cameras/freeCamera';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import { Button } from '@babylonjs/gui/2D/controls/button';
import { StackPanel } from '@babylonjs/gui/2D/controls/stackPanel';
import { ScrollViewer } from '@babylonjs/gui/2D/controls/scrollViewers/scrollViewer';
import { Slider } from '@babylonjs/gui/2D/controls/sliders/slider';
import { Ellipse } from '@babylonjs/gui/2D/controls/ellipse';
import { Grid } from '@babylonjs/gui/2D/controls/grid';
import { Control } from '@babylonjs/gui/2D/controls/control';
import { theme } from './theme';
import { renderDemoLoop } from './demo-loop';
import { Meter, MAX_BANKS } from '../engine/protocol';
import { REC_LENGTHS } from '../state/store';
import type { KpvStore } from '../state/store';
import { MAX_DECKS } from '../state/session';
import type { Session } from '../state/session';
import { TOUR_STEPS } from './tour';
import type { TourTargetName } from './tour';

const BANK_NAMES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const DECK_COLORS = [theme.accent, theme.accent2, theme.good, theme.warn];

interface TouchVis {
  marker: Ellipse;
  trail: Ellipse[];
  history: Array<[number, number]>;
}

interface DeckVis {
  root: Rectangle;
  pad: Rectangle;
  glow: Rectangle;
  beatDot: Ellipse;
  label: TextBlock;
  touches: [TouchVis, TouchVis];
  crosshairs: Array<[Rectangle, Rectangle]>;
}

/** Full-screen Babylon.js 9 GUI for a multi-deck KPV session. */
export class KpvUi {
  private engine3d: Engine;
  private scene: Scene;
  private adt: AdvancedDynamicTexture;

  private padsHost!: Rectangle;
  private padsGrid: Grid | null = null;
  private deckVis: DeckVis[] = [];
  private deckRow!: StackPanel;
  private deckChips: Button[] = [];
  private chainChip!: Button;
  private parallelChip!: Button;

  private titleText!: TextBlock;
  private axisText!: TextBlock;
  private fidelityDot!: Ellipse;
  private fidelityText!: TextBlock;
  private bpmText!: TextBlock;
  private holdBtn!: Button;
  private inputBtn!: Button;
  private meterBars: Rectangle[] = [];
  private programListPanel!: StackPanel;
  private statusText!: TextBlock;
  private depthSlider!: Slider;

  // Sampler panel
  private sampleModeBtn!: Button;
  private lengthChips: Button[] = [];
  private sourceChips: Button[] = [];
  private bankRow!: Grid;
  private bankBtns: Button[] = [];
  private bankProgress: Rectangle[] = [];
  private sliceGrid!: Grid;
  private sliceCells: Rectangle[] = [];
  private sliceWaveBars: Rectangle[][] = [];
  private sliceLabel!: TextBlock;
  private slice8Btn!: Button;
  private slice16Btn!: Button;
  private skipBtn!: Button;
  private muteBtn!: Button;
  private loopBtn!: Button;
  private muteBtnCtl!: Button;
  private t2Btn!: Button;
  private motionBtn!: Button;
  private monLabel!: Button;
  private monSlider!: Slider;
  private memoryChips: Button[] = [];
  private helpOverlay: Rectangle | null = null;

  // Guided tour
  private tourRefs = new Map<TourTargetName, Control>();
  private tourIdx = -1;
  private tourDim: Rectangle | null = null;
  private tourHighlight: Rectangle | null = null;
  private tourCard: Rectangle | null = null;
  private tourTitle: TextBlock | null = null;
  private tourBody: TextBlock | null = null;
  private tourCounter: TextBlock | null = null;
  private samplerHint!: TextBlock;
  private padHintDone = false;
  private tourPending = false;
  private framesRendered = 0;

  private holdActions = new Map<Button, { start: number; fill: Rectangle; done: () => void }>();
  private lastHoldFire = 0;
  private playheadShown = false;

  /** pointerId → which deck and which finger it drives. */
  private pointerMap = new Map<number, { deck: number; which: 1 | 2 }>();
  private lastCoords = new Map<string, [number, number]>();

  constructor(
    private canvas: HTMLCanvasElement,
    private session: Session
  ) {
    this.engine3d = new Engine(canvas, true, { adaptToDeviceRatio: false });
    this.scene = new Scene(this.engine3d);
    this.scene.clearColor = Color4.FromHexString(theme.bg + 'ff');
    const cam = new FreeCamera('cam', new Vector3(0, 0, -1), this.scene);
    cam.setTarget(Vector3.Zero());
    this.adt = AdvancedDynamicTexture.CreateFullscreenUI('ui', true, this.scene);

    this.buildLayout();
    this.bindPointerEvents();
    this.bindKeyboard();
    this.bindSession();
    for (const deck of this.session.decks) this.attachStore(deck);
    this.rebuildDecks();

    this.engine3d.runRenderLoop(() => {
      this.perFrame();
      this.scene.render();
    });
    window.addEventListener('resize', () => this.engine3d.resize());
  }

  /** All shared panels target the focused deck. */
  private get store(): KpvStore {
    return this.session.focused;
  }

  // ------------------------------------------------------------- primitives

  private panel(name: string): Rectangle {
    const r = new Rectangle(name);
    r.thickness = 1;
    r.color = theme.panelEdge;
    r.background = theme.panel;
    r.cornerRadius = 10;
    r.paddingLeft = '8px';
    r.paddingRight = '8px';
    r.paddingTop = '8px';
    r.paddingBottom = '8px';
    return r;
  }

  private label(text: string, size = 12, color: string = theme.textDim): TextBlock {
    const t = new TextBlock('', text);
    t.color = color;
    t.fontSize = size;
    t.height = `${size + 10}px`;
    return t;
  }

  private header(text: string): TextBlock {
    const t = this.label(text.toUpperCase(), 11, theme.accent);
    t.fontWeight = '600';
    return t;
  }

  private hoverize(b: Button, base: string, hover = '#2b2b3a'): void {
    b.onPointerEnterObservable.add(() => {
      if (b.background === base) b.background = hover;
    });
    b.onPointerOutObservable.add(() => {
      if (b.background === hover) b.background = base;
    });
  }

  private smallButton(text: string, onClick: () => void): Button {
    const b = Button.CreateSimpleButton('', text);
    b.height = '30px';
    b.color = theme.text;
    b.background = theme.panelEdge;
    b.cornerRadius = 6;
    b.thickness = 0;
    b.fontSize = 12;
    b.onPointerClickObservable.add(onClick);
    this.hoverize(b, theme.panelEdge);
    return b;
  }

  private chip(text: string, onClick: () => void): Button {
    const b = Button.CreateSimpleButton('', text);
    b.height = '24px';
    b.color = theme.textDim;
    b.background = 'transparent';
    b.cornerRadius = 12;
    b.thickness = 1;
    b.fontSize = 11;
    b.onPointerClickObservable.add(onClick);
    return b;
  }

  private paintChip(b: Button, active: boolean, activeColor: string = theme.accent): void {
    b.background = active ? activeColor : 'transparent';
    b.color = active ? theme.bg : theme.textDim;
  }

  /** Press-and-hold (1 s) with a red progress fill; releasing early cancels. */
  private makeHold(b: Button, done: () => void): void {
    const fill = new Rectangle();
    fill.thickness = 0;
    fill.background = theme.bad + '99';
    fill.width = '0%';
    fill.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    fill.isHitTestVisible = false;
    b.addControl(fill);
    b.onPointerDownObservable.add(() => {
      this.holdActions.set(b, { start: performance.now(), fill, done });
    });
    const cancel = (): void => {
      const h = this.holdActions.get(b);
      if (h) {
        h.fill.width = '0%';
        this.holdActions.delete(b);
      }
    };
    b.onPointerUpObservable.add(cancel);
    b.onPointerOutObservable.add(cancel);
  }

  private tickHolds(): void {
    for (const [b, h] of this.holdActions) {
      const p = (performance.now() - h.start) / 1000;
      h.fill.width = `${Math.min(100, p * 100)}%`;
      if (p >= 1) {
        h.fill.width = '0%';
        this.holdActions.delete(b);
        this.lastHoldFire = performance.now();
        h.done();
      }
    }
  }

  private clickAfterHold(): boolean {
    return performance.now() - this.lastHoldFire < 400;
  }

  // ------------------------------------------------------------------ layout

  private buildLayout(): void {
    const grid = new Grid('root');
    grid.addColumnDefinition(0.22);
    grid.addColumnDefinition(0.56);
    grid.addColumnDefinition(0.22);
    grid.addRowDefinition(1);
    this.adt.addControl(grid);

    const browser = this.buildBrowser();
    this.tourRefs.set('browser', browser);
    grid.addControl(browser, 0, 0);
    grid.addControl(this.buildCenter(), 0, 1);
    grid.addControl(this.buildSidebar(), 0, 2);
    this.buildHelpButton();
  }

  // Left: categories + program list ----------------------------------------

  private buildBrowser(): Control {
    const panel = this.panel('browser');
    const browserGrid = new Grid('browserGrid');
    browserGrid.addRowDefinition(24, true);
    browserGrid.addRowDefinition(132, true);
    browserGrid.addRowDefinition(1);
    browserGrid.addColumnDefinition(1);
    panel.addControl(browserGrid);

    browserGrid.addControl(this.header('Program'), 0, 0);

    const catGrid = new Grid('cats');
    for (let i = 0; i < 3; i++) catGrid.addColumnDefinition(1 / 3);
    for (let i = 0; i < 4; i++) catGrid.addRowDefinition(0.25);
    const cats = [
      'Filter', 'Color', 'Modulation', 'LFO', 'Delay', 'Reverb',
      'Looper', 'Kaoss', 'Vocal', 'Drum', 'Synth', 'ALL',
    ];
    cats.forEach((cat, i) => {
      const b = Button.CreateSimpleButton(`cat-${cat}`, cat === 'Modulation' ? 'Mod' : cat);
      b.color = theme.text;
      b.background = 'transparent';
      b.fontSize = 11;
      b.thickness = 0;
      b.cornerRadius = 5;
      b.paddingLeft = '1px';
      b.paddingRight = '1px';
      b.paddingTop = '1px';
      b.paddingBottom = '1px';
      b.onPointerClickObservable.add(() => this.populatePrograms(cat));
      this.hoverize(b, 'transparent');
      catGrid.addControl(b, Math.floor(i / 3), i % 3);
    });
    browserGrid.addControl(catGrid, 1, 0);

    const scroll = new ScrollViewer('programScroll');
    scroll.thickness = 0;
    scroll.barSize = 8;
    scroll.barColor = theme.panelEdge;
    browserGrid.addControl(scroll, 2, 0);

    this.programListPanel = new StackPanel('programList');
    this.programListPanel.isVertical = true;
    scroll.addControl(this.programListPanel);

    return panel;
  }

  private populatePrograms(category: string): void {
    this.programListPanel.clearControls();
    const data = this.session.data;
    if (!data) return;
    const list =
      category === 'ALL'
        ? data.programs
        : data.programs.filter((p) => p.category === category);
    for (const p of list) {
      const b = Button.CreateSimpleButton(`prog-${p.id}`, `${p.id}  ${p.name}`);
      b.height = '26px';
      b.color = theme.text;
      b.background = 'transparent';
      b.thickness = 0;
      b.cornerRadius = 5;
      b.fontSize = 12;
      if (b.textBlock) {
        b.textBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        b.textBlock.paddingLeft = '8px';
      }
      b.onPointerClickObservable.add(() => this.store.selectProgram(p.id));
      this.hoverize(b, 'transparent');
      this.programListPanel.addControl(b);
    }
  }

  // Center: header + pads + sampler --------------------------------------------

  private buildCenter(): Control {
    const grid = new Grid('center');
    grid.addRowDefinition(134, true);
    grid.addRowDefinition(1);
    grid.addRowDefinition(150, true);
    grid.addColumnDefinition(1);

    // Header
    const header = this.panel('header');
    const headerStack = new StackPanel();
    headerStack.isVertical = true;
    header.addControl(headerStack);

    // Deck row: one chip per KPV + add + routing mode.
    this.deckRow = new StackPanel();
    this.deckRow.isVertical = false;
    this.deckRow.height = '26px';
    headerStack.addControl(this.deckRow);

    const programInfo = new StackPanel();
    programInfo.isVertical = true;
    this.titleText = this.label('—', 19, theme.text);
    this.titleText.height = '28px';
    this.axisText = this.label('', 12, theme.textDim);
    this.axisText.height = '18px';
    const fidRow = new StackPanel();
    fidRow.isVertical = false;
    fidRow.height = '16px';
    this.fidelityDot = new Ellipse();
    this.fidelityDot.width = '10px';
    this.fidelityDot.height = '10px';
    this.fidelityDot.background = theme.textDim;
    this.fidelityDot.thickness = 0;
    this.fidelityText = this.label('', 11, theme.textDim);
    this.fidelityText.width = '560px';
    this.fidelityText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.fidelityText.paddingLeft = '8px';
    fidRow.addControl(this.fidelityDot);
    fidRow.addControl(this.fidelityText);
    programInfo.addControl(this.titleText);
    programInfo.addControl(this.axisText);
    programInfo.addControl(fidRow);
    headerStack.addControl(programInfo);
    this.tourRefs.set('programHeader', programInfo);
    this.tourRefs.set('deckRow', this.deckRow);

    const memRow = new StackPanel();
    memRow.isVertical = false;
    memRow.height = '24px';
    for (let i = 0; i < 8; i++) {
      const chipBtn = this.chip(`M${i + 1}`, () => {
        if (!this.clickAfterHold()) this.store.recallMemory(i);
      });
      chipBtn.width = '76px';
      this.makeHold(chipBtn, () => this.store.saveMemory(i));
      this.memoryChips.push(chipBtn);
      memRow.addControl(chipBtn);
    }
    headerStack.addControl(memRow);
    this.tourRefs.set('memories', memRow);
    grid.addControl(header, 0, 0);

    // Pads host — filled by rebuildDecks()
    this.padsHost = new Rectangle('padsHost');
    this.padsHost.thickness = 0;
    grid.addControl(this.padsHost, 1, 0);

    grid.addControl(this.buildSamplerPanel(), 2, 0);
    return grid;
  }

  // Deck management ------------------------------------------------------------

  private rebuildDeckRow(): void {
    this.deckRow.clearControls();
    this.deckChips = [];
    const label = this.header('Decks');
    label.width = '52px';
    label.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.deckRow.addControl(label);
    this.session.decks.forEach((_, i) => {
      const c = this.chip(`KPV ${i + 1}`, () => {
        if (!this.clickAfterHold()) this.session.focus(i);
      });
      c.width = '64px';
      if (this.session.decks.length > 1) {
        this.makeHold(c, () => this.session.removeDeck(i)); // hold = remove deck
      }
      this.deckChips.push(c);
      this.deckRow.addControl(c);
    });
    if (this.session.decks.length < MAX_DECKS) {
      const add = this.chip('+ DECK', () => {
        const store = this.session.addDeck();
        if (store) this.attachStore(store);
      });
      add.width = '64px';
      this.deckRow.addControl(add);
    }
    const spacer = this.label('', 10);
    spacer.width = '18px';
    this.deckRow.addControl(spacer);
    this.chainChip = this.chip('CHAIN', () => this.session.setRouting('chain'));
    this.chainChip.width = '62px';
    this.parallelChip = this.chip('PARALLEL', () => this.session.setRouting('parallel'));
    this.parallelChip.width = '78px';
    this.deckRow.addControl(this.chainChip);
    this.deckRow.addControl(this.parallelChip);
    this.paintDeckRow();
  }

  private paintDeckRow(): void {
    this.deckChips.forEach((c, i) => {
      this.paintChip(c, i === this.session.focusedIndex, DECK_COLORS[i % DECK_COLORS.length]!);
      if (c.textBlock && i !== this.session.focusedIndex) {
        c.color = DECK_COLORS[i % DECK_COLORS.length]!;
      }
    });
    this.paintChip(this.chainChip, this.session.routing === 'chain');
    this.paintChip(this.parallelChip, this.session.routing === 'parallel');
  }

  private makeTouchVis(color: string): TouchVis {
    const marker = new Ellipse();
    marker.width = '34px';
    marker.height = '34px';
    marker.thickness = 2;
    marker.color = color;
    marker.background = color + '55';
    marker.isVisible = false;
    const trail: Ellipse[] = [];
    for (let i = 0; i < 10; i++) {
      const t = new Ellipse();
      t.width = '12px';
      t.height = '12px';
      t.thickness = 0;
      t.background = color;
      t.alpha = 0;
      trail.push(t);
    }
    return { marker, trail, history: [] };
  }

  private buildDeckPad(deckIndex: number): DeckVis {
    const color = DECK_COLORS[deckIndex % DECK_COLORS.length]!;
    const root = new Rectangle(`deck-${deckIndex}`);
    root.thickness = 0;
    root.paddingLeft = '4px';
    root.paddingRight = '4px';
    root.paddingTop = '4px';
    root.paddingBottom = '4px';

    const pad = new Rectangle(`pad-${deckIndex}`);
    pad.thickness = 2;
    pad.color = theme.padGrid;
    pad.background = theme.padBg;
    pad.cornerRadius = 12;
    root.addControl(pad);

    const glow = new Rectangle();
    glow.thickness = 2;
    glow.color = color;
    glow.background = 'transparent';
    glow.cornerRadius = 12;
    glow.alpha = 0;
    glow.isHitTestVisible = false;
    pad.addControl(glow);

    for (let i = 1; i < 4; i++) {
      const v = new Rectangle();
      v.width = '1px';
      v.height = '100%';
      v.background = theme.padGrid;
      v.thickness = 0;
      v.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      v.left = `${i * 25}%`;
      v.isHitTestVisible = false;
      pad.addControl(v);
      const h = new Rectangle();
      h.height = '1px';
      h.width = '100%';
      h.background = theme.padGrid;
      h.thickness = 0;
      h.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
      h.top = `${i * 25}%`;
      h.isHitTestVisible = false;
      pad.addControl(h);
    }

    const label = this.label('', 12, color);
    label.height = '18px';
    label.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    label.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    label.left = '10px';
    label.top = '6px';
    label.isHitTestVisible = false;
    pad.addControl(label);

    const beatDot = new Ellipse();
    beatDot.width = '10px';
    beatDot.height = '10px';
    beatDot.background = color;
    beatDot.thickness = 0;
    beatDot.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    beatDot.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    beatDot.top = '8px';
    beatDot.left = '-8px';
    beatDot.isHitTestVisible = false;
    pad.addControl(beatDot);

    const crosshairs: Array<[Rectangle, Rectangle]> = [];
    for (const colr of [theme.accent, theme.accent2]) {
      const hLine = new Rectangle();
      hLine.height = '1px';
      hLine.width = '100%';
      hLine.thickness = 0;
      hLine.background = colr;
      hLine.alpha = 0;
      hLine.isHitTestVisible = false;
      const vLine = new Rectangle();
      vLine.width = '1px';
      vLine.height = '100%';
      vLine.thickness = 0;
      vLine.background = colr;
      vLine.alpha = 0;
      vLine.isHitTestVisible = false;
      pad.addControl(hLine);
      pad.addControl(vLine);
      crosshairs.push([hLine, vLine]);
    }

    const touches: [TouchVis, TouchVis] = [
      this.makeTouchVis(theme.accent),
      this.makeTouchVis(theme.accent2),
    ];
    for (const tv of touches) {
      for (const t of tv.trail) pad.addControl(t);
      pad.addControl(tv.marker);
    }

    // First-run nudge in the middle of the pad; disappears once audio flows.
    if (deckIndex === 0 && !this.padHintDone) {
      const hint = this.label('touch & drag to play — DEMO or CONNECT INPUT gives you sound', 13);
      hint.name = 'padHint';
      hint.isHitTestVisible = false;
      pad.addControl(hint);
    }

    return { root, pad, glow, beatDot, label, touches, crosshairs };
  }

  /** Lay pads out 1×1, 2×1, or 2×2 and rebuild deck chips. */
  private rebuildDecks(): void {
    if (this.padsGrid) this.padsHost.removeControl(this.padsGrid);
    this.deckVis = [];
    const n = this.session.decks.length;
    const cols = n > 1 ? 2 : 1;
    const rows = n > 2 ? 2 : 1;
    const g = new Grid('padsGrid');
    for (let c = 0; c < cols; c++) g.addColumnDefinition(1 / cols);
    for (let r = 0; r < rows; r++) g.addRowDefinition(1 / rows);
    for (let i = 0; i < n; i++) {
      const vis = this.buildDeckPad(i);
      this.deckVis.push(vis);
      g.addControl(vis.root, Math.floor(i / cols), i % cols);
    }
    this.padsGrid = g;
    this.padsHost.addControl(g);
    this.rebuildDeckRow();
    this.paintDeckLabels();
    this.refreshFocusedPanels();
  }

  private paintDeckLabels(): void {
    this.session.decks.forEach((deck, i) => {
      const vis = this.deckVis[i];
      if (!vis) return;
      const cur = deck.current;
      vis.label.text = `KPV ${i + 1}  ·  ${cur ? `${cur.entry.id} ${cur.entry.name}` : '—'}`;
      vis.pad.color = i === this.session.focusedIndex ? DECK_COLORS[i % DECK_COLORS.length]! : theme.padGrid;
      vis.pad.thickness = i === this.session.focusedIndex ? 2 : 1;
    });
  }

  // Sampler panel ---------------------------------------------------------------

  private buildSamplerPanel(): Control {
    const panel = this.panel('sampler');
    const g = new Grid('samplerGrid');
    g.addColumnDefinition(1);
    g.addRowDefinition(28, true);
    g.addRowDefinition(46, true);
    g.addRowDefinition(1);
    panel.addControl(g);

    const modeRow = new StackPanel();
    modeRow.isVertical = false;
    modeRow.height = '26px';
    modeRow.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;

    const title = this.header('Sampler');
    title.width = '78px';
    title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    modeRow.addControl(title);

    this.sampleModeBtn = this.chip('● SAMPLE', () => this.store.setSamplingMode(!this.store.samplingMode));
    this.sampleModeBtn.width = '84px';
    modeRow.addControl(this.sampleModeBtn);

    const lenLabel = this.label('LEN', 10);
    lenLabel.width = '34px';
    modeRow.addControl(lenLabel);
    for (const beats of REC_LENGTHS) {
      const c = this.chip(String(beats), () => this.store.setRecBeats(beats));
      c.width = '34px';
      this.lengthChips.push(c);
      modeRow.addControl(c);
    }

    const srcLabel = this.label('SRC', 10);
    srcLabel.width = '36px';
    modeRow.addControl(srcLabel);
    const inChip = this.chip('IN', () => this.store.setRecSource('input'));
    inChip.width = '40px';
    const reChip = this.chip('RESAMPLE', () => this.store.setRecSource('output'));
    reChip.width = '84px';
    this.sourceChips = [inChip, reChip];
    modeRow.addControl(inChip);
    modeRow.addControl(reChip);

    const demo = this.chip('DEMO', () => this.loadDemoLoop());
    demo.width = '56px';
    modeRow.addControl(demo);

    // Contextual hint — tells you the next step of the sampling flow.
    this.samplerHint = this.label('', 10);
    this.samplerHint.width = '330px';
    this.samplerHint.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.samplerHint.paddingLeft = '10px';
    modeRow.addControl(this.samplerHint);

    g.addControl(modeRow, 0, 0);
    this.tourRefs.set('samplerMode', modeRow);

    this.bankRow = new Grid('banks');
    this.bankRow.addRowDefinition(1);
    g.addControl(this.bankRow, 1, 0);
    this.tourRefs.set('banks', this.bankRow);
    this.rebuildBankRow();

    const sliceRow = new Grid('sliceRow');
    sliceRow.addRowDefinition(1);
    sliceRow.addColumnDefinition(300, true);
    sliceRow.addColumnDefinition(1);
    g.addControl(sliceRow, 2, 0);
    this.tourRefs.set('slices', sliceRow);

    const sliceCtl = new StackPanel();
    sliceCtl.isVertical = false;
    sliceCtl.height = '24px';
    this.sliceLabel = this.label('A', 12, theme.text);
    this.sliceLabel.width = '18px';
    sliceCtl.addControl(this.sliceLabel);
    this.slice8Btn = this.chip('8', () => this.store.setSliceCount(this.store.selectedBank, 8));
    this.slice8Btn.width = '26px';
    this.slice16Btn = this.chip('16', () => this.store.setSliceCount(this.store.selectedBank, 16));
    this.slice16Btn.width = '30px';
    this.skipBtn = this.chip('SKIP', () => this.store.setSliceMode(this.store.selectedBank, 'skip'));
    this.skipBtn.width = '44px';
    this.muteBtn = this.chip('MUTE', () => this.store.setSliceMode(this.store.selectedBank, 'mute'));
    this.muteBtn.width = '46px';
    this.loopBtn = this.chip('LOOP', () =>
      this.store.setLoopMode(this.store.selectedBank, !this.store.bankStates[this.store.selectedBank]!.loop)
    );
    this.loopBtn.width = '44px';
    const clear = this.chip('✕ HOLD', () => undefined);
    clear.width = '54px';
    this.makeHold(clear, () => this.store.clearBank(this.store.selectedBank));
    sliceCtl.addControl(this.slice8Btn);
    sliceCtl.addControl(this.slice16Btn);
    sliceCtl.addControl(this.skipBtn);
    sliceCtl.addControl(this.muteBtn);
    sliceCtl.addControl(this.loopBtn);
    sliceCtl.addControl(clear);
    sliceRow.addControl(sliceCtl, 0, 0);

    this.sliceGrid = new Grid('slices');
    this.sliceGrid.addRowDefinition(1);
    sliceRow.addControl(this.sliceGrid, 0, 1);
    this.rebuildSliceGrid();

    return panel;
  }

  private rebuildBankRow(): void {
    this.bankRow.clearControls();
    while (this.bankRow.columnCount > 0) this.bankRow.removeColumnDefinition(0);
    const n = this.store.visibleBanks;
    const showAdd = n < MAX_BANKS;
    const cols = n + (showAdd ? 1 : 0);
    for (let i = 0; i < cols; i++) this.bankRow.addColumnDefinition(1 / cols);

    this.bankBtns = [];
    this.bankProgress = [];
    for (let i = 0; i < n; i++) {
      const b = Button.CreateSimpleButton(`bank-${i}`, BANK_NAMES[i]!);
      b.color = theme.text;
      b.background = theme.panelEdge;
      b.cornerRadius = 8;
      b.thickness = 0;
      b.fontSize = 16;
      b.paddingLeft = '3px';
      b.paddingRight = '3px';
      b.paddingTop = '4px';
      b.paddingBottom = '4px';
      b.onPointerClickObservable.add(() => this.store.bankPress(i));
      const strip = new Rectangle();
      strip.height = '3px';
      strip.width = '0%';
      strip.thickness = 0;
      strip.background = theme.accent2;
      strip.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      strip.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      strip.isHitTestVisible = false;
      b.addControl(strip);
      this.bankBtns.push(b);
      this.bankProgress.push(strip);
      this.bankRow.addControl(b, 0, i);
    }
    if (showAdd) {
      const add = Button.CreateSimpleButton('bank-add', '+');
      add.color = theme.textDim;
      add.background = 'transparent';
      add.thickness = 1;
      add.cornerRadius = 8;
      add.fontSize = 18;
      add.paddingLeft = '3px';
      add.paddingRight = '3px';
      add.paddingTop = '4px';
      add.paddingBottom = '4px';
      add.onPointerClickObservable.add(() => this.store.addBank());
      this.bankRow.addControl(add, 0, n);
    }
  }

  private rebuildSliceGrid(): void {
    this.sliceGrid.clearControls();
    while (this.sliceGrid.columnCount > 0) this.sliceGrid.removeColumnDefinition(0);
    const bs = this.store.bankStates[this.store.selectedBank]!;
    const count = bs.slices;
    for (let i = 0; i < count; i++) this.sliceGrid.addColumnDefinition(1 / count);
    this.sliceCells = [];
    this.sliceWaveBars = [];
    for (let i = 0; i < count; i++) {
      const cell = new Rectangle(`slice-${i}`);
      cell.thickness = 1;
      cell.color = theme.panelEdge;
      cell.cornerRadius = 4;
      cell.paddingLeft = '2px';
      cell.paddingRight = '2px';
      cell.paddingTop = '3px';
      cell.paddingBottom = '3px';
      cell.background = (bs.mask >>> i) & 1 ? theme.accent + 'cc' : theme.padBg;
      cell.isPointerBlocker = true;
      cell.onPointerClickObservable.add(() => this.store.toggleSlice(this.store.selectedBank, i));

      const barsPerCell = 64 / count;
      const bars: Rectangle[] = [];
      for (let k = 0; k < barsPerCell; k++) {
        const bar = new Rectangle();
        bar.thickness = 0;
        bar.background = '#ffffff2e';
        bar.width = `${Math.floor(88 / barsPerCell)}%`;
        bar.height = '10%';
        bar.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        bar.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        bar.left = `${6 + (k * 88) / barsPerCell}%`;
        bar.isHitTestVisible = false;
        cell.addControl(bar);
        bars.push(bar);
      }
      this.sliceWaveBars.push(bars);

      this.sliceCells.push(cell);
      this.sliceGrid.addControl(cell, 0, i);
    }
    this.repaintWaveform();
  }

  private repaintWaveform(): void {
    const peaks = this.store.overviews[this.store.selectedBank];
    const count = this.sliceCells.length;
    if (count === 0) return;
    const barsPerCell = 64 / count;
    for (let i = 0; i < count; i++) {
      const bars = this.sliceWaveBars[i];
      if (!bars) continue;
      for (let k = 0; k < bars.length; k++) {
        const p = peaks ? peaks[Math.floor(i * barsPerCell + k)]! : 0;
        bars[k]!.height = `${Math.max(6, Math.round(Math.sqrt(Math.min(1, p)) * 90))}%`;
      }
    }
  }

  private refreshSamplerPanel(): void {
    this.paintChip(this.sampleModeBtn, this.store.samplingMode);
    if (this.sampleModeBtn.textBlock) {
      this.sampleModeBtn.textBlock.text = this.store.samplingMode ? '● PICK BANK' : '● SAMPLE';
    }
    if (this.store.samplingMode) this.sampleModeBtn.background = theme.bad;
    REC_LENGTHS.forEach((beats, i) => this.paintChip(this.lengthChips[i]!, this.store.recBeats === beats));
    this.paintChip(this.sourceChips[0]!, this.store.recSource === 'input');
    this.paintChip(this.sourceChips[1]!, this.store.recSource === 'output');
    if (this.bankBtns.length !== this.store.visibleBanks) this.rebuildBankRow();
    const bs = this.store.bankStates[this.store.selectedBank]!;
    this.sliceLabel.text = BANK_NAMES[this.store.selectedBank]!;
    this.paintChip(this.slice8Btn, bs.slices === 8);
    this.paintChip(this.slice16Btn, bs.slices === 16);
    this.paintChip(this.skipBtn, bs.mode === 'skip');
    this.paintChip(this.muteBtn, bs.mode === 'mute');
    this.paintChip(this.loopBtn, bs.loop);
    if (this.loopBtn.textBlock) this.loopBtn.textBlock.text = bs.loop ? 'LOOP' : 'ONE';

    // Contextual next-step hint for the sampling flow.
    const recBank = Array.from({ length: this.store.visibleBanks }, (_, b) => b).find(
      (b) => this.store.bankState(b) === 3
    );
    this.samplerHint.text =
      recBank !== undefined
        ? `● recording ${BANK_NAMES[recBank]} — press it again to punch out`
        : this.store.samplingMode
          ? this.session.tempoSet
            ? `armed — press a bank to record ${this.store.recBeats} beats${this.store.recSource === 'output' ? ' of what you hear' : ''}`
            : 'armed — press a bank, play, press again to stop: that take sets the tempo'
          : 'S arms sampling · bank letters play/stop · drag audio files in';
    if (this.sliceCells.length !== bs.slices) this.rebuildSliceGrid();
    else {
      this.sliceCells.forEach((cell, i) => {
        cell.background = (bs.mask >>> i) & 1 ? theme.accent + 'cc' : theme.padBg;
      });
      this.repaintWaveform();
    }
  }

  // Right: transport + levels -------------------------------------------------

  private buildSidebar(): Control {
    const panel = this.panel('side');
    const grid = new Grid('sideGrid');
    grid.addColumnDefinition(1);
    grid.addRowDefinition(116, true);
    grid.addRowDefinition(48, true);
    grid.addRowDefinition(78, true);
    grid.addRowDefinition(1);
    grid.addRowDefinition(168, true);
    grid.addRowDefinition(92, true);
    panel.addControl(grid);

    const bpmStack = new StackPanel();
    bpmStack.isVertical = true;
    bpmStack.addControl(this.header('BPM · all decks'));
    this.bpmText = this.label('120.0', 26, theme.text);
    this.bpmText.height = '36px';
    bpmStack.addControl(this.bpmText);
    const bpmRow = new StackPanel();
    bpmRow.isVertical = false;
    bpmRow.height = '32px';
    const minus = this.smallButton('−', () => this.session.setBpm(this.session.bpm - 1));
    minus.width = '32px';
    const tap = this.smallButton('TAP', () => this.session.tapTempo());
    tap.width = '64px';
    tap.color = theme.accent;
    const plus = this.smallButton('+', () => this.session.setBpm(this.session.bpm + 1));
    plus.width = '32px';
    bpmRow.addControl(minus);
    bpmRow.addControl(tap);
    bpmRow.addControl(plus);
    bpmStack.addControl(bpmRow);
    grid.addControl(bpmStack, 0, 0);
    this.tourRefs.set('bpm', bpmStack);

    const perfRow = new Grid('perfRow');
    perfRow.addRowDefinition(1);
    perfRow.addColumnDefinition(0.38);
    perfRow.addColumnDefinition(0.38);
    perfRow.addColumnDefinition(0.24);
    this.holdBtn = Button.CreateSimpleButton('hold', 'HOLD');
    this.holdBtn.color = theme.text;
    this.holdBtn.background = theme.panelEdge;
    this.holdBtn.cornerRadius = 8;
    this.holdBtn.thickness = 0;
    this.holdBtn.height = '40px';
    this.holdBtn.paddingRight = '3px';
    this.holdBtn.onPointerClickObservable.add(() => this.store.setHold(!this.store.hold));
    this.hoverize(this.holdBtn, theme.panelEdge);
    perfRow.addControl(this.holdBtn, 0, 0);

    this.muteBtnCtl = Button.CreateSimpleButton('mute', 'MUTE');
    this.muteBtnCtl.color = theme.text;
    this.muteBtnCtl.background = theme.panelEdge;
    this.muteBtnCtl.cornerRadius = 8;
    this.muteBtnCtl.thickness = 0;
    this.muteBtnCtl.height = '40px';
    this.muteBtnCtl.paddingLeft = '3px';
    this.muteBtnCtl.onPointerDownObservable.add(() => this.store.setMute(true));
    this.muteBtnCtl.onPointerUpObservable.add(() => this.store.setMute(false));
    this.muteBtnCtl.onPointerOutObservable.add(() => this.store.setMute(false));
    perfRow.addControl(this.muteBtnCtl, 0, 1);

    this.t2Btn = Button.CreateSimpleButton('t2', 'T2');
    this.t2Btn.color = theme.textDim;
    this.t2Btn.background = theme.panelEdge;
    this.t2Btn.cornerRadius = 8;
    this.t2Btn.thickness = 0;
    this.t2Btn.height = '40px';
    this.t2Btn.paddingLeft = '3px';
    this.t2Btn.onPointerClickObservable.add(() =>
      this.store.setTwoFingerMode(!this.store.twoFingerMode)
    );
    this.hoverize(this.t2Btn, theme.panelEdge);
    perfRow.addControl(this.t2Btn, 0, 2);
    grid.addControl(perfRow, 1, 0);
    this.tourRefs.set('perfRow', perfRow);

    const motionStack = new StackPanel();
    motionStack.isVertical = true;
    this.motionBtn = this.chip('● MOTION', () => {
      if (!this.clickAfterHold()) this.store.toggleMotionRecord();
    });
    this.motionBtn.width = '110px';
    this.motionBtn.height = '26px';
    this.makeHold(this.motionBtn, () => {
      this.store.stopMotionPlayback();
      this.store.motion = null;
    });
    motionStack.addControl(this.motionBtn);
    motionStack.addControl(this.header('FX Release'));
    const rel = new Slider('fxRelease');
    rel.minimum = 0;
    rel.maximum = 1;
    rel.value = this.store.fxRelease;
    rel.height = '16px';
    rel.width = '90%';
    rel.color = theme.accent2;
    rel.background = theme.panelEdge;
    rel.onValueChangedObservable.add((v) => this.store.setFxRelease(v));
    motionStack.addControl(rel);
    grid.addControl(motionStack, 2, 0);
    this.tourRefs.set('motionRelease', motionStack);

    const depthStack = new StackPanel();
    depthStack.isVertical = true;
    depthStack.addControl(this.header('FX Depth'));
    this.depthSlider = new Slider('fxDepth');
    this.depthSlider.minimum = 0;
    this.depthSlider.maximum = 1;
    this.depthSlider.value = 1;
    this.depthSlider.isVertical = true;
    this.depthSlider.width = '40px';
    this.depthSlider.height = '82%';
    this.depthSlider.color = theme.accent;
    this.depthSlider.background = theme.panelEdge;
    this.depthSlider.onValueChangedObservable.add((v) => this.store.setFxDepth(v));
    depthStack.addControl(this.depthSlider);
    grid.addControl(depthStack, 3, 0);
    this.tourRefs.set('fxDepth', depthStack);

    const meterStack = new StackPanel();
    meterStack.isVertical = true;
    meterStack.addControl(this.header('In / Out'));
    const meterRow = new StackPanel();
    meterRow.isVertical = false;
    meterRow.height = '84px';
    meterRow.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    for (let i = 0; i < 4; i++) {
      const holder = new Rectangle();
      holder.width = '16px';
      holder.height = '80px';
      holder.thickness = 1;
      holder.color = theme.panelEdge;
      holder.background = theme.padBg;
      holder.paddingLeft = '2px';
      holder.paddingRight = '2px';
      const bar = new Rectangle();
      bar.width = '100%';
      bar.height = '0%';
      bar.thickness = 0;
      bar.background = i < 2 ? theme.accent2 : theme.accent;
      bar.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      holder.addControl(bar);
      this.meterBars.push(bar);
      meterRow.addControl(holder);
    }
    meterStack.addControl(meterRow);

    const gains = new Grid('gains');
    gains.height = '58px';
    gains.addColumnDefinition(1 / 3);
    gains.addColumnDefinition(1 / 3);
    gains.addColumnDefinition(1 / 3);
    gains.addRowDefinition(18, true);
    gains.addRowDefinition(1);
    gains.addControl(this.label('IN GAIN', 9), 0, 0);
    // MON label doubles as the on/off toggle (also: N key).
    this.monLabel = this.chip('MON', () => this.store.toggleMonitor());
    this.monLabel.width = '44px';
    this.monLabel.height = '18px';
    this.monLabel.fontSize = 9;
    gains.addControl(this.monLabel, 0, 1);
    gains.addControl(this.label('OUT VOL', 9), 0, 2);
    const inGain = new Slider('inGain');
    inGain.minimum = 0;
    inGain.maximum = 2;
    inGain.value = 1;
    inGain.height = '16px';
    inGain.width = '86%';
    inGain.color = theme.accent2;
    inGain.background = theme.panelEdge;
    inGain.onValueChangedObservable.add((v) => this.store.engine.setInputGain(v));
    gains.addControl(inGain, 1, 0);
    this.monSlider = new Slider('monitor');
    this.monSlider.minimum = 0;
    this.monSlider.maximum = 2;
    this.monSlider.value = 1;
    this.monSlider.height = '16px';
    this.monSlider.width = '86%';
    this.monSlider.color = theme.good;
    this.monSlider.background = theme.panelEdge;
    this.monSlider.onValueChangedObservable.add((v) => this.store.setMonitorLevel(v));
    gains.addControl(this.monSlider, 1, 1);
    const outVol = new Slider('outVol');
    outVol.minimum = 0;
    outVol.maximum = 1.5;
    outVol.value = 1;
    outVol.height = '16px';
    outVol.width = '86%';
    outVol.color = theme.accent;
    outVol.background = theme.panelEdge;
    outVol.onValueChangedObservable.add((v) => this.store.engine.setOutputGain(v));
    gains.addControl(outVol, 1, 2);
    meterStack.addControl(gains);
    grid.addControl(meterStack, 4, 0);
    this.tourRefs.set('metersGains', meterStack);

    const inputStack = new StackPanel();
    inputStack.isVertical = true;
    this.inputBtn = this.smallButton('CONNECT INPUT', () => void this.session.connectInput());
    this.inputBtn.height = '34px';
    inputStack.addControl(this.inputBtn);
    this.statusText = this.label('', 10, theme.textDim);
    this.statusText.height = '44px';
    this.statusText.textWrapping = true;
    inputStack.addControl(this.statusText);
    grid.addControl(inputStack, 5, 0);
    this.tourRefs.set('input', inputStack);

    return panel;
  }

  // -------------------------------------------------------------- pad input

  /** Canvas coords → { deck, x, y } for whichever pad the pointer is over. */
  private toPad(e: PointerEvent, clampToDeck: number | null): { deck: number; x: number; y: number } | null {
    const rect = this.canvas.getBoundingClientRect();
    const sx = this.engine3d.getRenderWidth() / rect.width;
    const sy = this.engine3d.getRenderHeight() / rect.height;
    const px = (e.clientX - rect.left) * sx;
    const py = (e.clientY - rect.top) * sy;
    const decks = clampToDeck !== null ? [clampToDeck] : this.deckVis.map((_, i) => i);
    for (const i of decks) {
      const vis = this.deckVis[i];
      if (!vis) continue;
      const m = (vis.pad as unknown as { _currentMeasure: { left: number; top: number; width: number; height: number } })
        ._currentMeasure;
      let x = (px - m.left) / m.width;
      let y = 1 - (py - m.top) / m.height;
      if (clampToDeck === null && (x < 0 || x > 1 || y < 0 || y > 1)) continue;
      x = Math.min(1, Math.max(0, x));
      y = Math.min(1, Math.max(0, y));
      return { deck: i, x, y };
    }
    return null;
  }

  private sendTouch(deck: number, which: 1 | 2, down: boolean, x: number, y: number): void {
    this.lastCoords.set(`${deck}:${which}`, [x, y]);
    const store = this.session.decks[deck];
    if (!store) return;
    if (which === 1) store.pad1(down, x, y);
    else store.pad2(down, x, y);
  }

  private bindPointerEvents(): void {
    this.canvas.addEventListener('pointerdown', (e) => {
      const p = this.toPad(e, null);
      if (!p) return;
      const store = this.session.decks[p.deck];
      if (!store) return;
      // Touching a pad focuses its deck — panels follow your hands.
      this.session.focus(p.deck);
      const inUse = new Set(
        [...this.pointerMap.values()].filter((v) => v.deck === p.deck).map((v) => v.which)
      );
      let which: 1 | 2;
      if (store.twoFingerMode) {
        which = e.shiftKey && !inUse.has(2) ? 2 : inUse.has(1) ? 2 : 1;
        if (inUse.has(which)) return;
      } else {
        if (inUse.has(1)) return;
        which = 1;
      }
      this.pointerMap.set(e.pointerId, { deck: p.deck, which });
      this.canvas.setPointerCapture(e.pointerId);
      this.sendTouch(p.deck, which, true, p.x, p.y);
      e.preventDefault();
    });
    this.canvas.addEventListener('pointermove', (e) => {
      const entry = this.pointerMap.get(e.pointerId);
      if (!entry) return;
      const p = this.toPad(e, entry.deck);
      if (p) this.sendTouch(entry.deck, entry.which, true, p.x, p.y);
      e.preventDefault();
    });
    const release = (e: PointerEvent): void => {
      const entry = this.pointerMap.get(e.pointerId);
      if (!entry) return;
      this.pointerMap.delete(e.pointerId);
      const last = this.lastCoords.get(`${entry.deck}:${entry.which}`) ?? [0.5, 0.5];
      this.sendTouch(entry.deck, entry.which, false, last[0], last[1]);
    };
    this.canvas.addEventListener('pointerup', release);
    this.canvas.addEventListener('pointercancel', release);
  }

  /**
   * Keyboard map (press ? for the overlay). Everything targets the focused
   * deck except tempo (global) and Tab (switches decks).
   */
  private bindKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      // While the tour is open it owns the keyboard: Enter/→ next, ← back,
      // Esc closes. Nothing leaks through to the instrument.
      if (this.tourIdx >= 0) {
        if (e.code === 'Enter' || e.code === 'ArrowRight' || e.code === 'Space') {
          this.showTourStep(this.tourIdx + 1);
        } else if (e.code === 'ArrowLeft') {
          this.showTourStep(this.tourIdx - 1);
        } else if (e.code === 'Escape') {
          this.endTour();
        }
        e.preventDefault();
        return;
      }
      if (e.repeat && e.code !== 'Comma' && e.code !== 'Period' && !e.code.startsWith('Bracket')) return;
      if (e.code === 'Tab') {
        this.session.focusNext();
        e.preventDefault();
        return;
      }
      if (e.code >= 'Digit1' && e.code <= 'Digit8') {
        const n = Number(e.code.slice(-1)) - 1;
        if (e.altKey) {
          if (n < this.store.visibleBanks) this.store.selectBank(n);
        } else if (e.shiftKey) {
          this.store.toggleSlice(this.store.selectedBank, n);
        } else if (e.ctrlKey || e.metaKey) {
          if (this.store.bankStates[this.store.selectedBank]!.slices === 16) {
            this.store.toggleSlice(this.store.selectedBank, n + 8);
          }
        } else if (n < this.store.visibleBanks) {
          this.store.bankPress(n);
        }
        e.preventDefault();
        return;
      }
      switch (e.code) {
        case 'Space':
          this.store.setHold(!this.store.hold);
          e.preventDefault();
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          this.store.selectRelative(1);
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          this.store.selectRelative(-1);
          break;
        case 'KeyT':
          this.session.tapTempo();
          break;
        case 'KeyS':
          this.store.setSamplingMode(!this.store.samplingMode);
          break;
        case 'KeyM':
          this.store.setMute(true);
          break;
        case 'KeyR':
          this.store.toggleMotionRecord();
          break;
        case 'KeyF':
          this.store.setTwoFingerMode(!this.store.twoFingerMode);
          break;
        case 'KeyN':
          this.store.toggleMonitor();
          break;
        case 'Comma':
          this.session.setBpm(this.session.bpm - (e.shiftKey ? 0.1 : 1));
          break;
        case 'Period':
          this.session.setBpm(this.session.bpm + (e.shiftKey ? 0.1 : 1));
          break;
        case 'BracketLeft':
          this.store.setFxDepth(this.store.fxDepth - 0.05);
          break;
        case 'BracketRight':
          this.store.setFxDepth(this.store.fxDepth + 0.05);
          break;
        case 'Semicolon':
          this.store.setFxRelease(this.store.fxRelease - 0.05);
          break;
        case 'Quote':
          this.store.setFxRelease(this.store.fxRelease + 0.05);
          break;
        case 'Slash':
          if (e.shiftKey) this.toggleHelp();
          break;
        case 'Escape':
          if (this.helpOverlay) this.toggleHelp();
          else if (this.tourIdx >= 0) this.endTour();
          break;
      }
    });
    window.addEventListener('keyup', (e) => {
      if (e.code === 'KeyM') this.store.setMute(false);
    });
  }

  // ------------------------------------------------------------- guided tour

  /** The floating ? in the corner — replays the walkthrough anytime. */
  private buildHelpButton(): void {
    const b = Button.CreateSimpleButton('tourBtn', '?');
    b.width = '34px';
    b.height = '34px';
    b.cornerRadius = 17;
    b.thickness = 1;
    b.color = theme.accent;
    b.background = theme.panel;
    b.fontSize = 16;
    b.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    b.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    b.left = '-10px';
    b.top = '-10px';
    b.zIndex = 50;
    b.onPointerClickObservable.add(() => this.startTour());
    this.hoverize(b, theme.panel);
    this.adt.addControl(b);
  }

  private startTour(): void {
    if (this.tourIdx >= 0) return;
    this.tourDim = new Rectangle('tourDim');
    this.tourDim.width = '100%';
    this.tourDim.height = '100%';
    this.tourDim.background = '#000000';
    this.tourDim.alpha = 0.45;
    this.tourDim.thickness = 0;
    this.tourDim.isPointerBlocker = true;
    this.tourDim.zIndex = 90;
    this.adt.addControl(this.tourDim);

    this.tourHighlight = new Rectangle('tourHi');
    this.tourHighlight.thickness = 3;
    this.tourHighlight.color = theme.accent;
    this.tourHighlight.cornerRadius = 10;
    this.tourHighlight.background = 'transparent';
    this.tourHighlight.isHitTestVisible = false;
    this.tourHighlight.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.tourHighlight.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.tourHighlight.zIndex = 95;
    this.adt.addControl(this.tourHighlight);

    const card = new Rectangle('tourCard');
    card.width = '460px';
    card.height = '300px';
    card.background = theme.panel;
    card.color = theme.accent;
    card.thickness = 1;
    card.cornerRadius = 12;
    card.isPointerBlocker = true;
    card.zIndex = 100;
    const stack = new StackPanel();
    stack.isVertical = true;
    stack.paddingLeft = '20px';
    stack.paddingRight = '20px';
    card.addControl(stack);
    this.tourTitle = this.label('', 16, theme.accent);
    this.tourTitle.height = '34px';
    this.tourTitle.width = '414px';
    this.tourTitle.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    stack.addControl(this.tourTitle);
    this.tourBody = this.label('', 12, theme.text);
    this.tourBody.height = '196px';
    this.tourBody.width = '414px';
    this.tourBody.textWrapping = true;
    this.tourBody.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.tourBody.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    stack.addControl(this.tourBody);
    const nav = new StackPanel();
    nav.isVertical = false;
    nav.height = '34px';
    this.tourCounter = this.label('', 11);
    this.tourCounter.width = '70px';
    const back = this.smallButton('◀ Back', () => this.showTourStep(this.tourIdx - 1));
    back.width = '80px';
    const next = this.smallButton('Next ▶', () => this.showTourStep(this.tourIdx + 1));
    next.width = '110px';
    next.color = theme.accent;
    const skip = this.smallButton('✕ Close', () => this.endTour());
    skip.width = '80px';
    nav.addControl(this.tourCounter);
    nav.addControl(back);
    nav.addControl(next);
    nav.addControl(skip);
    stack.addControl(nav);
    this.tourCard = card;
    this.adt.addControl(card);

    this.showTourStep(0);
  }

  private endTour(): void {
    for (const c of [this.tourDim, this.tourHighlight, this.tourCard]) {
      if (c) this.adt.removeControl(c);
    }
    this.tourDim = this.tourHighlight = this.tourCard = null;
    this.tourIdx = -1;
    try {
      localStorage.setItem('kpv-tour-done', '1');
    } catch {
      /* fine */
    }
  }

  private tourTarget(name: TourTargetName): Control | null {
    // The pad target follows the focused deck.
    if (name === 'pad') return this.deckVis[this.session.focusedIndex]?.pad ?? null;
    return this.tourRefs.get(name) ?? null;
  }

  private showTourStep(idx: number): void {
    if (idx < 0) idx = 0;
    if (idx >= TOUR_STEPS.length) {
      this.endTour();
      return;
    }
    this.tourIdx = idx;
    const step = TOUR_STEPS[idx]!;
    if (this.tourTitle) this.tourTitle.text = step.title;
    if (this.tourBody) this.tourBody.text = step.body;
    if (this.tourCounter) this.tourCounter.text = `${idx + 1} / ${TOUR_STEPS.length}`;
    this.positionTour();
  }

  /** Place highlight over the step's target and the card away from it. */
  private positionTour(): void {
    if (this.tourIdx < 0 || !this.tourHighlight || !this.tourCard) return;
    const step = TOUR_STEPS[this.tourIdx]!;
    const target = step.target ? this.tourTarget(step.target) : null;
    if (!target) {
      this.tourHighlight.isVisible = false;
      this.tourCard.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
      this.tourCard.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      this.tourCard.top = '0px';
      return;
    }
    const m = (target as unknown as { _currentMeasure: { left: number; top: number; width: number; height: number } })
      ._currentMeasure;
    this.tourHighlight.isVisible = true;
    this.tourHighlight.leftInPixels = m.left - 6;
    this.tourHighlight.topInPixels = m.top - 6;
    this.tourHighlight.widthInPixels = m.width + 12;
    this.tourHighlight.heightInPixels = m.height + 12;
    // Card goes wherever the target isn't.
    const canvasH = this.engine3d.getRenderHeight();
    const targetMidY = m.top + m.height / 2;
    this.tourCard.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    this.tourCard.verticalAlignment =
      targetMidY > canvasH / 2 ? Control.VERTICAL_ALIGNMENT_TOP : Control.VERTICAL_ALIGNMENT_BOTTOM;
    this.tourCard.top = targetMidY > canvasH / 2 ? '40px' : '-40px';
  }

  private toggleHelp(): void {
    if (this.helpOverlay) {
      this.adt.removeControl(this.helpOverlay);
      this.helpOverlay = null;
      return;
    }
    const overlay = new Rectangle('help');
    overlay.width = '540px';
    overlay.height = '500px';
    overlay.background = theme.panel + 'f6';
    overlay.color = theme.accent;
    overlay.thickness = 1;
    overlay.cornerRadius = 12;
    overlay.isPointerBlocker = true;
    overlay.onPointerClickObservable.add(() => this.toggleHelp());
    const text = new TextBlock('helpText');
    text.color = theme.text;
    text.fontSize = 13;
    text.fontFamily = 'ui-monospace, monospace';
    text.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    text.paddingLeft = '24px';
    text.paddingTop = '16px';
    text.text = [
      'KEYBOARD — click anywhere to close',
      '',
      'DECKS',
      '  Tab             switch focused deck',
      '  touch a pad     also focuses that deck',
      '  + DECK chip     add a KPV (hold a chip to remove it)',
      '  CHAIN/PARALLEL  deck routing (chain = pads stack)',
      '',
      'PAD (focused deck)',
      '  drag            play (finger 1)',
      '  F               2-finger mode on/off',
      '  Shift-drag      finger 2 (when 2-finger on)',
      '  Space           HOLD',
      '  M (hold down)   MUTE — dry cuts, FX tails ring',
      '  N               mic monitor on/off (MON slider = level)',
      '',
      'PROGRAM',
      '  ← → ↑ ↓         previous / next program',
      '  [ / ]           FX depth − / +',
      "  ; / '           FX release − / +",
      '',
      'TEMPO (all decks)',
      '  T               tap tempo',
      '  , / .           BPM −1 / +1   (Shift = ±0.1)',
      '',
      'SAMPLER (focused deck)',
      '  S               sampling mode (then press a bank)',
      '  1–8             play / stop bank',
      '  Alt+1–8         select bank (no trigger)',
      '  Shift+1–8       toggle slices 1–8',
      '  Ctrl+1–8        toggle slices 9–16',
      '  R               record / stop pad motion',
    ].join('\n');
    overlay.addControl(text);
    this.adt.addControl(overlay);
    this.helpOverlay = overlay;
  }

  // ------------------------------------------------------------------ state

  private bindSession(): void {
    this.session.on('decks', () => {
      if (this.deckVis.length !== this.session.decks.length) this.rebuildDecks();
      else {
        this.paintDeckRow();
        this.paintDeckLabels();
        this.refreshFocusedPanels();
      }
    });
    this.session.on('tempo', () => {
      this.bpmText.text = this.session.bpm.toFixed(1);
    });
    this.session.on('input', () => this.paintInput());
  }

  /** Subscribe a deck's events; shared panels refresh only when it's focused. */
  attachStore(store: KpvStore): void {
    const ifFocused = (fn: () => void) => () => {
      if (store === this.session.focused) fn();
    };
    store.on('program', () => {
      this.paintDeckLabels();
      ifFocused(() => this.paintProgram())();
    });
    store.on('transport', ifFocused(() => this.paintPerf()));
    store.on('hold', ifFocused(() => this.paintPerf()));
    store.on('banks', ifFocused(() => this.refreshSamplerPanel()));
  }

  private paintProgram(): void {
    const cur = this.store.current;
    if (!cur) return;
    this.titleText.text = `${cur.entry.id}  ${cur.entry.name}`;
    const t2 = cur.entry.touch2;
    this.axisText.text =
      `X: ${cur.entry.xAxis}    Y: ${cur.entry.yAxis}` +
      (t2 ? `    T2: ${t2.label ?? '—'}${t2.x2Axis ? ` (X2 ${t2.x2Axis}` : ''}${t2.y2Axis ? ` / Y2 ${t2.y2Axis})` : t2.x2Axis ? ')' : ''}` : '');
    const f = cur.resolved.fidelity;
    this.fidelityDot.background = f === 'full' ? theme.good : f === 'partial' ? theme.warn : theme.bad;
    const notes: string[] = [];
    if (cur.resolved.missingUnits.length) notes.push(`stubbed: ${cur.resolved.missingUnits.join(', ')}`);
    if (cur.resolved.unresolvedAxes.length) notes.push(`unmapped: ${cur.resolved.unresolvedAxes.join('; ')}`);
    this.fidelityText.text = notes.length ? notes.join('  ·  ') : 'full mapping';
    this.paintMemories();
  }

  private paintPerf(): void {
    this.holdBtn.background = this.store.hold ? theme.accent : theme.panelEdge;
    this.holdBtn.color = this.store.hold ? theme.bg : theme.text;
    this.t2Btn.background = this.store.twoFingerMode ? theme.accent2 : theme.panelEdge;
    this.t2Btn.color = this.store.twoFingerMode ? theme.bg : theme.textDim;
    this.depthSlider.value = this.store.fxDepth;
    this.paintChip(this.monLabel, this.store.monitorOn, theme.good);
    if (this.monLabel.textBlock) {
      this.monLabel.textBlock.text = this.store.monitorOn ? 'MON' : 'MON ✕';
    }
    this.monSlider.value = this.store.monitorLevel;
  }

  private paintInput(): void {
    const label = this.session.system.inputLabel;
    if (this.inputBtn.textBlock) {
      this.inputBtn.textBlock.text = label ? `IN: ${label.slice(0, 22)}` : 'CONNECT INPUT';
    }
    this.inputBtn.color = label ? theme.good : theme.text;
  }

  private paintMemories(): void {
    this.memoryChips.forEach((chipBtn, i) => {
      const mem = this.store.memories[i];
      if (chipBtn.textBlock) chipBtn.textBlock.text = mem ? `M${i + 1}·${mem.programId}` : `M${i + 1}`;
      this.paintChip(chipBtn, !!mem && mem.programId === this.store.current?.entry.id);
    });
  }

  /** Repaint everything that targets the focused deck (after focus change). */
  private refreshFocusedPanels(): void {
    this.paintProgram();
    this.paintPerf();
    this.paintInput();
    this.refreshSamplerPanel();
  }

  private loadDemoLoop(): void {
    const { l, r, beats } = renderDemoLoop(this.store.engine.sampleRate, this.session.bpm);
    this.store.engine.loadSampleData(0, l, r, beats, true);
    this.store.engine.sampleTrigger(0, true);
    this.statusText.text = `Demo loop in KPV ${this.session.focusedIndex + 1} bank A — touch the pad.`;
  }

  private perFrame(): void {
    this.tickHolds();
    if (this.tourIdx >= 0) this.positionTour();

    // First-visit tour: start only after the UI has demonstrably rendered.
    if (this.tourPending && ++this.framesRendered >= 24) {
      this.tourPending = false;
      this.startTour();
    }

    // Retire the pad hint once anything is audible.
    if (!this.padHintDone) {
      const m0 = this.session.decks[0]?.engine.meters;
      if (m0 && (m0[Meter.OutL]! > 0.02 || m0[Meter.OutR]! > 0.02)) {
        this.padHintDone = true;
        const hint = this.deckVis[0]?.pad.getChildByName('padHint');
        if (hint) this.deckVis[0]!.pad.removeControl(hint);
      }
    }

    // Per-deck: motion, touch markers, glow, beat dot.
    this.session.decks.forEach((deck, di) => {
      const liveTouching = [...this.pointerMap.values()].some((v) => v.deck === di);
      deck.motionTick(liveTouching);
      const vis = this.deckVis[di];
      if (!vis) return;
      const c = deck.engine.ctrl;
      const m = deck.engine.meters;
      const states: Array<[boolean, number, number]> = [
        [c[0]! > 0.5, c[1]!, c[2]!],
        [c[3]! > 0.5, c[4]!, c[5]!],
      ];
      for (let i = 0; i < 2; i++) {
        const [active, x, y] = states[i]!;
        const tv = vis.touches[i]!;
        const [hLine, vLine] = vis.crosshairs[i]!;
        tv.marker.isVisible = active;
        hLine.alpha = active ? 0.18 : 0;
        vLine.alpha = active ? 0.18 : 0;
        if (active) {
          tv.marker.left = `${(x - 0.5) * 100}%`;
          tv.marker.top = `${(0.5 - y) * 100}%`;
          hLine.top = `${(0.5 - y) * 100}%`;
          vLine.left = `${(x - 0.5) * 100}%`;
          tv.history.unshift([x, y]);
          if (tv.history.length > 10) tv.history.pop();
        } else if (tv.history.length) {
          tv.history.pop();
        }
        tv.trail.forEach((t, j) => {
          const h = tv.history[j];
          if (h) {
            t.alpha = 0.5 * (1 - j / 10);
            t.left = `${(h[0] - 0.5) * 100}%`;
            t.top = `${(0.5 - h[1]) * 100}%`;
          } else {
            t.alpha = 0;
          }
        });
      }
      vis.glow.alpha = Math.min(1, m[Meter.Wet]! * 0.9);
      vis.beatDot.alpha = Math.max(0.15, 1 - m[Meter.BeatPhase]! * 1.6);
    });

    // Focused-deck panels: motion button, mute, meters, banks, slice playhead.
    const m = this.store.engine.meters;
    if (this.motionBtn.textBlock) {
      const label = this.store.motionRecording
        ? '● REC…'
        : this.store.motionPlaying
          ? '▶ MOTION'
          : this.store.motion
            ? '▷ MOTION'
            : '● MOTION';
      if (this.motionBtn.textBlock.text !== label) this.motionBtn.textBlock.text = label;
      this.motionBtn.background = this.store.motionRecording
        ? theme.bad
        : this.store.motionPlaying
          ? theme.good
          : 'transparent';
      this.motionBtn.color =
        this.store.motionRecording || this.store.motionPlaying ? theme.bg : theme.textDim;
    }
    this.muteBtnCtl.background = this.store.mute ? theme.bad : theme.panelEdge;

    const levels = [m[Meter.InL]!, m[Meter.InR]!, m[Meter.OutL]!, m[Meter.OutR]!];
    levels.forEach((v, i) => {
      const bar = this.meterBars[i]!;
      bar.height = `${Math.min(100, Math.sqrt(Math.min(1, v)) * 100)}%`;
      bar.background = v > 0.98 ? theme.bad : i < 2 ? theme.accent2 : theme.accent;
    });

    for (let i = 0; i < this.bankBtns.length; i++) {
      const st = m[Meter.SampleBankState + i]!;
      const btn = this.bankBtns[i]!;
      const selected = i === this.store.selectedBank;
      btn.background =
        st === 3 ? theme.bad : st === 2 ? theme.good : st === 1 ? '#3a2f14' : theme.panelEdge;
      btn.color = st === 2 ? theme.bg : theme.text;
      btn.thickness = selected ? 1 : 0;
      btn.color = selected && st !== 2 ? theme.accent2 : btn.color;
      const strip = this.bankProgress[i]!;
      strip.width = `${Math.round(m[Meter.SamplePos + i]! * 100)}%`;
      strip.background = st === 3 ? theme.bad : theme.accent2;
    }

    const selState = m[Meter.SampleBankState + this.store.selectedBank]!;
    if (selState === 2 && this.sliceCells.length > 0) {
      const pos = m[Meter.SamplePos + this.store.selectedBank]!;
      const idx = Math.min(this.sliceCells.length - 1, Math.floor(pos * this.sliceCells.length));
      const bs = this.store.bankStates[this.store.selectedBank]!;
      this.sliceCells.forEach((cell, i) => {
        const on = (bs.mask >>> i) & 1;
        cell.background = i === idx
          ? on ? theme.accent : '#3a3a4a'
          : on ? theme.accent + 'cc' : theme.padBg;
        cell.color = i === idx ? theme.text : theme.panelEdge;
      });
      this.playheadShown = true;
    } else if (this.playheadShown) {
      this.playheadShown = false;
      this.refreshSamplerPanel();
    }
  }

  /** Called once after boot. */
  ready(): void {
    this.populatePrograms('Filter');
    this.refreshFocusedPanels();
    const iso = crossOriginIsolated ? 'SAB bridge' : 'postMessage bridge';
    this.statusText.text = `${Math.round(this.session.system.sampleRate / 1000)}k · ${iso} · ${this.session.system.contextLatencyMs.toFixed(1)} ms out · ? bottom-right = tour · Shift+/ = keys`;
    // First visit: auto-start the walkthrough — but only once the interface
    // has actually rendered (frame-counted in perFrame, not a blind timer).
    try {
      this.tourPending = localStorage.getItem('kpv-tour-done') !== '1';
    } catch {
      this.tourPending = true;
    }
  }
}
